import Dexie, { type EntityTable } from "dexie";
import type { CanonicalCase } from "../../domain/case/canonical-case";

export interface CachedCaseRecord extends CanonicalCase {
  cacheKey: string;
  ownerScope: string;
  searchText: string;
}

export interface LocalPreferenceRecord {
  key: string;
  value: unknown;
  updatedAt: string;
}

export interface UndoJournalRecord {
  id: string;
  ownerScope: string;
  kind: "edit" | "bulk-edit" | "delete" | "archive" | "import" | "create-list";
  label: string;
  createdAt: string;
  expiresAt: string;
  before: CanonicalCase[];
  affectedCaseIds: string[];
}

export interface LocalImportRecord {
  id: string;
  ownerScope: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  payload: unknown;
}

class StudioDatabase extends Dexie {
  cases!: EntityTable<CachedCaseRecord, "cacheKey">;
  preferences!: EntityTable<LocalPreferenceRecord, "key">;
  undoJournal!: EntityTable<UndoJournalRecord, "id">;
  imports!: EntityTable<LocalImportRecord, "id">;

  constructor() {
    super("LogBookAnaesStudio");
    this.version(1).stores({
      cases: "&cacheKey,ownerScope,id,date,updatedAt,isDeleted,importBatchId",
      preferences: "&key,updatedAt",
      undoJournal: "&id,ownerScope,kind,createdAt,expiresAt,*affectedCaseIds",
      imports: "&id,ownerScope,status,createdAt,updatedAt",
    });
  }
}

export const studioDb = new StudioDatabase();

export function workspaceScope(uid: string | null | undefined): string {
  return uid ? `uid:${encodeURIComponent(uid)}` : "offline";
}

function searchableText(value: CanonicalCase): string {
  return [
    value.date,
    value.session,
    value.hospital,
    value.data.procedure,
    value.data.spec,
    value.data.asa,
    value.data.urg,
    value.data.maint,
    value.data.airway,
    value.data.sup,
    value.data.notes,
    ...value.data.regional,
    ...value.data.access_new,
  ].join("\u001f").toLocaleLowerCase("en-GB");
}

export async function replaceCachedCases(
  ownerScope: string,
  cases: CanonicalCase[],
): Promise<void> {
  const records = cases.map((item): CachedCaseRecord => ({
    ...item,
    cacheKey: `${ownerScope}:${item.id}`,
    ownerScope,
    searchText: searchableText(item),
  }));
  await studioDb.transaction("rw", studioDb.cases, async () => {
    await studioDb.cases.where("ownerScope").equals(ownerScope).delete();
    await studioDb.cases.bulkPut(records);
  });
}

export async function putCachedCases(
  ownerScope: string,
  cases: CanonicalCase[],
): Promise<void> {
  await studioDb.cases.bulkPut(cases.map((item): CachedCaseRecord => ({
    ...item,
    cacheKey: `${ownerScope}:${item.id}`,
    ownerScope,
    searchText: searchableText(item),
  })));
}

export async function readCachedCases(ownerScope: string): Promise<CanonicalCase[]> {
  const values = await studioDb.cases.where("ownerScope").equals(ownerScope).toArray();
  return values.map(({ cacheKey: _cacheKey, ownerScope: _ownerScope, searchText: _searchText, ...item }) => item);
}
